import { useId, useState } from "react";
import type {
  AuthorizationItem,
  ConnectorDefinition,
} from "../../../shared/connectors/types";
import { Button } from "../Button";
import { ButtonLink } from "../Button/ButtonLink";
import { ConnectorImage } from "../ConnectorImage";
import { Icon } from "../Icon";
import { Input } from "../Input";
import { RefreshSpin } from "../RefreshSpin";
import { CopyableValue } from "../Settings/CopyableValue";
import { Text } from "../Text";
import { AuthorizedButton, AuthorizedLabel, CheckIcon } from "./authorizedChip";
import type { App } from "./Form";
import { useConnectorAuthorization } from "./useConnectorAuthorization";

export function ConnectorAuthorizationFieldItem({
  app,
  item,
  definition,
  disabled,
}: {
  app: App;
  item: AuthorizationItem;
  definition: ConnectorDefinition;
  disabled?: boolean;
}) {
  const emailId = useId();
  const [email, setEmail] = useState(
    app.configuration.connectorAuthorizationEmail ?? ""
  );
  const auth = useConnectorAuthorization(app, disabled);
  const configuration = app.configuration;
  const status = configuration.connectorAuthorizationStatus;
  const registering = status === "registering" && !auth.interrupted;
  const connected =
    app.secretKeys?.includes(item.target.field) &&
    !["registering", "awaitingApproval", "checking", "failed"].includes(
      status ?? ""
    );
  const code = configuration.connectorAuthorizationCode;
  const verificationUrl = configuration.connectorAuthorizationUrl;
  const hasChallenge =
    !auth.interrupted &&
    (status === "awaitingApproval" || status === "checking") &&
    code &&
    verificationUrl?.startsWith("https://");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {connected && (
        <AuthorizedButton style={{ padding: "6px 20px" }}>
          <AuthorizedLabel>
            <ConnectorImage src={definition.logo} alt={definition.name} />
            <Text variant="text-regular-md">
              Connected to {definition.name}
            </Text>
            <CheckIcon />
          </AuthorizedLabel>
        </AuthorizedButton>
      )}
      <label htmlFor={emailId}>
        <Text variant="text-small-md">{item.emailLabel}</Text>
      </label>
      <Input
        id={emailId}
        type="email"
        value={email}
        disabled={disabled || auth.pending}
        placeholder="you@yourcompany.com"
        onChange={(event) => setEmail(event.target.value)}
      />
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          type="button"
          variant={hasChallenge || connected ? "secondary" : "primary"}
          size="l"
          disabled={
            disabled ||
            auth.pending ||
            registering ||
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
          }
          icon={registering ? "refresh-cw" : undefined}
          iconSpinning={registering}
          onClick={() => auth.start(email)}
        >
          {registering
            ? "Generating code…"
            : connected
              ? "Replace authorization"
              : status
                ? "Generate a new code"
                : "Generate verification code"}
        </Button>
      </div>
      {hasChallenge && (
        <>
          <Text variant="text-regular">
            Open {definition.name}, sign in as{" "}
            {configuration.connectorAuthorizationEmail}, and enter this code to
            approve access.
          </Text>
          <CopyableValue value={code!}>
            <Text variant="text-regular-md">{code}</Text>
          </CopyableValue>
          {auth.copied && (
            <Text variant="text-small" color="var(--text-secondary)">
              Code copied to clipboard
            </Text>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            {auth.polling && (
              <div
                role="status"
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <RefreshSpin spinning>
                  <Icon name="refresh-cw" size={16} />
                </RefreshSpin>
                <Text variant="text-small" color="var(--text-secondary)">
                  Waiting for confirmation…
                </Text>
              </div>
            )}
            <ButtonLink
              to={verificationUrl!}
              target="_blank"
              rel="noopener noreferrer"
              variant={auth.polling ? "subtle" : "primary"}
              size="l"
              icon="arrow-up-right"
              onClick={auth.openVerification}
            >
              {auth.polling
                ? `Open ${definition.name} again`
                : `Open ${definition.name} to verify`}
            </ButtonLink>
          </div>
        </>
      )}
      {(auth.error || configuration.connectorAuthorizationError) && (
        <div role="alert">
          <Text variant="text-small" color="var(--text-error)">
            {auth.error || configuration.connectorAuthorizationError}
          </Text>
        </div>
      )}
    </div>
  );
}
